import Config from 'react-native-config';
import type { CaptureSource } from '../types/faceAttendance';

const captureSources: readonly CaptureSource[] = ['KIOSK', 'API', 'HR_PORTAL'];

function finiteNumber(name: string, value: string | undefined): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) throw new Error(`${name} is invalid`);
  return parsed;
}

function positiveInteger(name: string, value: string | undefined): number {
  const parsed = finiteNumber(name, value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${name} must be a positive integer`);
  }
  return parsed;
}

export function getFaceAttendanceConfig() {
  const captureQuality = finiteNumber(
    'FACE_ATTENDANCE_CAPTURE_QUALITY',
    Config.FACE_ATTENDANCE_CAPTURE_QUALITY,
  );
  const imageMaxWidth = positiveInteger(
    'FACE_ATTENDANCE_IMAGE_MAX_WIDTH',
    Config.FACE_ATTENDANCE_IMAGE_MAX_WIDTH,
  );
  const imageMaxHeight = positiveInteger(
    'FACE_ATTENDANCE_IMAGE_MAX_HEIGHT',
    Config.FACE_ATTENDANCE_IMAGE_MAX_HEIGHT,
  );
  const pollIntervalMs = positiveInteger(
    'FACE_ATTENDANCE_LIVENESS_RESULT_POLL_INTERVAL_MS',
    Config.FACE_ATTENDANCE_LIVENESS_RESULT_POLL_INTERVAL_MS,
  );
  const resultTimeoutMs = positiveInteger(
    'FACE_ATTENDANCE_LIVENESS_RESULT_TIMEOUT_MS',
    Config.FACE_ATTENDANCE_LIVENESS_RESULT_TIMEOUT_MS,
  );
  const captureSource = Config.FACE_ATTENDANCE_DEFAULT_CAPTURE_SOURCE;
  const requireLivenessRaw = String(Config.FACE_ATTENDANCE_REQUIRE_LIVENESS || 'false')
    .trim()
    .toLowerCase();
  const requireLiveness = requireLivenessRaw === 'true' || requireLivenessRaw === '1';

  if (captureQuality <= 0 || captureQuality > 1) {
    throw new Error('FACE_ATTENDANCE_CAPTURE_QUALITY must be greater than 0 and at most 1');
  }
  if (pollIntervalMs < 500) {
    throw new Error('FACE_ATTENDANCE_LIVENESS_RESULT_POLL_INTERVAL_MS must be at least 500');
  }
  if (resultTimeoutMs <= pollIntervalMs) {
    throw new Error('FACE_ATTENDANCE_LIVENESS_RESULT_TIMEOUT_MS must exceed the poll interval');
  }
  if (!captureSources.includes(captureSource as CaptureSource)) {
    throw new Error('FACE_ATTENDANCE_DEFAULT_CAPTURE_SOURCE is unsupported');
  }

  return {
    captureQuality,
    imageMaxWidth,
    imageMaxHeight,
    pollIntervalMs,
    resultTimeoutMs,
    captureSource: captureSource as CaptureSource,
    requireLiveness,
  } as const;
}
